import clsx from 'clsx';
import type { WNBATeam, WNBATeamMetrics } from '../../../types';

function MetricRow({
  label,
  away,
  home,
  higherIsBetter = true,
  format = (v: number) => v.toFixed(1),
}: {
  label: string;
  away?: number;
  home?: number;
  higherIsBetter?: boolean;
  format?: (v: number) => string;
}) {
  const awayWins = away !== undefined && home !== undefined
    ? (higherIsBetter ? away > home : away < home)
    : false;
  const homeWins = away !== undefined && home !== undefined
    ? (higherIsBetter ? home > away : home < away)
    : false;

  return (
    <div className="flex items-center text-xs py-1.5 border-b border-surface-4 last:border-0">
      <span className={clsx('w-1/3 tabular-nums text-right pr-4', awayWins ? 'text-win font-semibold' : 'text-gray-300')}>
        {away !== undefined ? format(away) : '—'}
      </span>
      <span className="w-1/3 text-center text-gray-500 font-medium">{label}</span>
      <span className={clsx('w-1/3 tabular-nums text-left pl-4', homeWins ? 'text-win font-semibold' : 'text-gray-300')}>
        {home !== undefined ? format(home) : '—'}
      </span>
    </div>
  );
}

function TrendArrow({ last5, last10 }: { last5?: number; last10?: number }) {
  if (last5 === undefined || last10 === undefined) return null;
  const trending = last5 > last10;
  return (
    <span className={clsx('ml-1 text-xs', trending ? 'text-win' : 'text-loss')}>
      {trending ? '↑' : '↓'}
    </span>
  );
}

interface TeamMetricsPanelProps {
  awayTeam: WNBATeam;
  homeTeam: WNBATeam;
  awayTeamMetrics?: WNBATeamMetrics;
  homeTeamMetrics?: WNBATeamMetrics;
}

export function TeamMetricsPanel({
  awayTeam,
  homeTeam,
  awayTeamMetrics,
  homeTeamMetrics,
}: TeamMetricsPanelProps) {
  if (!awayTeamMetrics && !homeTeamMetrics) {
    return (
      <div className="text-center py-8 text-gray-600 text-sm">
        No team metrics available
      </div>
    );
  }

  const awayOff = awayTeamMetrics?.awayOffRating ?? awayTeamMetrics?.offRating;
  const awayDef = awayTeamMetrics?.awayDefRating ?? awayTeamMetrics?.defRating;
  const homeOff = homeTeamMetrics?.homeOffRating ?? homeTeamMetrics?.offRating;
  const homeDef = homeTeamMetrics?.homeDefRating ?? homeTeamMetrics?.defRating;

  return (
    <div className="space-y-3">
      {/* Column headers */}
      <div className="flex items-center text-xs font-semibold">
        <span className="w-1/3 text-right pr-4 text-gray-300 truncate">{awayTeam.abbreviation}</span>
        <span className="w-1/3 text-center text-gray-600">Metric</span>
        <span className="w-1/3 text-left pl-4 text-gray-300 truncate">{homeTeam.abbreviation}</span>
      </div>

      <div className="rounded-lg border border-surface-4 bg-surface-2 overflow-hidden px-3">
        <MetricRow
          label="Off Rtg"
          away={awayOff}
          home={homeOff}
        />
        <MetricRow
          label="Def Rtg"
          away={awayDef}
          home={homeDef}
          higherIsBetter={false}
        />
        <MetricRow
          label="Net Rtg"
          away={awayTeamMetrics?.netRating}
          home={homeTeamMetrics?.netRating}
        />
        <MetricRow
          label="Pace"
          away={awayTeamMetrics?.pace}
          home={homeTeamMetrics?.pace}
          higherIsBetter={true}
        />
        <MetricRow
          label="Reb %"
          away={awayTeamMetrics?.reboundRate}
          home={homeTeamMetrics?.reboundRate}
          format={(v) => `${(v * 100).toFixed(1)}%`}
        />
        <MetricRow
          label="TO %"
          away={awayTeamMetrics?.toRate}
          home={homeTeamMetrics?.toRate}
          higherIsBetter={false}
          format={(v) => `${(v * 100).toFixed(1)}%`}
        />
      </div>

      {/* Form trend */}
      <div className="rounded-lg border border-surface-4 bg-surface-2 overflow-hidden px-3">
        <div className="flex items-center text-xs py-1.5 border-b border-surface-4">
          <span className="w-1/3 text-right pr-4 text-gray-300 tabular-nums">
            {awayTeamMetrics?.last5NetRating?.toFixed(1) ?? '—'}
            <TrendArrow last5={awayTeamMetrics?.last5NetRating} last10={awayTeamMetrics?.last10NetRating} />
          </span>
          <span className="w-1/3 text-center text-gray-500 font-medium">L5 Net</span>
          <span className="w-1/3 text-left pl-4 text-gray-300 tabular-nums">
            {homeTeamMetrics?.last5NetRating?.toFixed(1) ?? '—'}
            <TrendArrow last5={homeTeamMetrics?.last5NetRating} last10={homeTeamMetrics?.last10NetRating} />
          </span>
        </div>
        <MetricRow
          label="L10 Net"
          away={awayTeamMetrics?.last10NetRating}
          home={homeTeamMetrics?.last10NetRating}
        />
      </div>
    </div>
  );
}
